import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

/**
 * UX-level copy protection for the public storefront.
 *
 * This raises the effort of casually lifting page content — dragging a selection, right-click >
 * Copy, Ctrl+C. It is NOT security, and nothing here should ever be treated as such: the content is
 * in the server-rendered HTML by design (see the SEO note below), so view-source, devtools, the
 * network tab, reader mode and a screenshot all still reach it. Anything that actually hid the text
 * would hide it from Googlebot too, which would cost far more than the copying does.
 *
 * What it deliberately does NOT do:
 *
 *   - It does not change the markup. Every heading, link and schema block ships exactly as before,
 *     so crawling and indexing are untouched.
 *   - It does not run on the server. Every listener is attached behind an `isPlatformBrowser`
 *     check, so the SSR build has no `document` or `window` to reach for.
 *   - It does not block programmatic clipboard writes. The "Copy Code" buttons use
 *     `navigator.clipboard.writeText()`, which is a different path from the `copy` event this
 *     cancels — so the one copy the site intends to offer keeps working, untouched.
 *
 * The escape hatches all funnel through `isEditable`. Anywhere a person is meant to type or select
 * — inputs, textareas, selects, contenteditable regions, and anything marked `data-allow-select` —
 * behaves exactly as it did before.
 */
@Injectable({ providedIn: 'root' })
export class CopyProtectionService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  private started = false;

  /**
   * Elements where the browser's own behaviour has to survive.
   *
   * A form field the user cannot select inside is a broken form field: they cannot correct a typo,
   * cannot select-all to replace what they typed, and on some platforms cannot reach the paste
   * menu. `data-allow-select` is the same exemption for anything else that needs it later.
   */
  private isEditable(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;

    return Boolean(
      target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"], [data-allow-select]'),
    );
  }

  init(): void {
    // SSR renders the same HTML either way; there is simply no document to attach to there.
    if (!isPlatformBrowser(this.platformId) || this.started) return;
    this.started = true;

    const doc = this.document;

    // --- selection-based copying ---------------------------------------------------------------
    // Cancelling `copy`/`cut` covers every route into them at once — the keyboard, the context
    // menu, the Edit menu — so this is the backstop rather than the keydown handler below.
    for (const type of ['copy', 'cut'] as const) {
      doc.addEventListener(type, (event: Event) => {
        if (this.isEditable(event.target)) return;
        event.preventDefault();
      });
    }

    // --- keyboard shortcuts --------------------------------------------------------------------
    doc.addEventListener('keydown', (event: KeyboardEvent) => {
      // metaKey is Cmd on macOS; ctrlKey covers Windows and Linux.
      if (!event.ctrlKey && !event.metaKey) return;
      if (this.isEditable(event.target)) return;

      // `key` rather than `keyCode`: it respects the active layout, and it is the property that is
      // not deprecated. Lowercased because Shift is held for the capital form.
      if (['c', 'x', 'a'].includes(event.key.toLowerCase())) {
        event.preventDefault();
      }
    });

    // --- right-click ---------------------------------------------------------------------------
    // Left available inside form fields: that menu is where spellcheck suggestions and Paste live,
    // and taking it away breaks typing rather than protecting anything.
    doc.addEventListener('contextmenu', (event: MouseEvent) => {
      if (this.isEditable(event.target)) return;
      event.preventDefault();
    });

    // --- dragging ------------------------------------------------------------------------------
    // Dragging an image to the desktop saves it, and dragging a selection carries the text with it.
    // Real drag interactions opt back in with `[draggable="true"]`; there are none today, but the
    // check costs nothing and keeps this from silently breaking one added later.
    doc.addEventListener('dragstart', (event: DragEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest('[draggable="true"]')) return;
      if (this.isEditable(target)) return;
      event.preventDefault();
    });
  }
}
